import {
    formatDayMonth,
    generateCalendarDays,
    getWeekParity,
    isWeekOdd,
} from '../../utils/dateUtils';

/**
 * The data side of CalendarSchedule: which days, periods and cards the calendar shows, and which
 * neighbouring cards it draws as one grouped cell.
 *
 * <p>Kept free of React so the table and the xlsx export read the schedule through the same rules
 * and cannot drift apart.
 */

// Which cards belong in a slot. A term answers by the parity of the week the date falls in; an
// exam session answers by the date itself, since two of its Saturdays can share a parity. Keeping
// it a parameter is what lets both render through the same table.
export const parityCardsFor = (startDay) => (calendarDay, classItem) => {
    const isOdd = isWeekOdd(getWeekParity(startDay, calendarDay.date));
    return isOdd ? classItem.cards.odd : classItem.cards.even;
};

export const findDayData = (resultArray, calendarDay) =>
    resultArray.find((d) => d.day === calendarDay.dayName);

/** The periods of a day that have at least one card on that date. */
export const nonEmptyClasses = (calendarDay, dayData, cardsFor) =>
    dayData
        ? dayData.classes.filter(
            (classItem) => !cardsFor(calendarDay, classItem).every((g) => g.card === null),
        )
        : [];

/** The dates the calendar shows: the whole semester or just today, minus days with no lessons. */
export const visibleCalendarDays = ({ semester, resultArray }, viewMode, cardsFor) => {
    const allCalendarDays = generateCalendarDays(semester.startDay, semester.endDay);

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayFormatted = formatDayMonth(today);

    const calendarDays =
        viewMode === 'today'
            ? allCalendarDays.filter((d) => d.dateFormatted === todayFormatted)
            : allCalendarDays;

    return calendarDays.filter(
        (calendarDay) =>
            nonEmptyClasses(calendarDay, findDayData(resultArray, calendarDay), cardsFor).length > 0,
    );
};

const sameLesson = (a, b) =>
    a !== null &&
    b !== null &&
    a.subject?.id === b.subject?.id &&
    a.teacher?.id === b.teacher?.id &&
    a.room?.id === b.room?.id &&
    a.lessonType === b.lessonType;

/**
 * Splits one slot's per-group cards into runs drawn as single cells: neighbouring groups with the
 * same subject, teacher, room and lesson type share one cell spanning all of them.
 *
 * <p>The table used to work this out by setting skip_render on the cards themselves. This leaves
 * the cards untouched, so rendering and exporting cannot leak state into each other.
 *
 * @returns [{ card, index, span }] where index is the position of the run's first group
 */
export const groupSlotCards = (groups) => {
    const runs = [];
    let index = 0;

    while (index < groups.length) {
        const { card } = groups[index];
        let span = 1;
        while (index + span < groups.length && sameLesson(card, groups[index + span].card)) {
            span += 1;
        }
        runs.push({ card, index, span });
        index += span;
    }

    return runs;
};

const toMinutes = (time) => {
    const [hours, minutes] = String(time ?? '').split(':').map(Number);
    return Number.isNaN(hours) ? Infinity : hours * 60 + (minutes || 0);
};

/**
 * The periods the xlsx export lays out under every date: from the earliest period holding a lesson
 * on any of the given dates to the latest, gaps included, so each date gets the same rows.
 *
 * <p>Periods are collected from every weekday of the schedule, not only the exported ones, and
 * matched by id: a week-based payload need not list every period under every weekday, and a gap
 * in the middle of the range must still get its row. They are ordered by start time rather than
 * by the order the server sent them in.
 *
 * <p>The table keeps showing only the periods that hold something; this is for the export alone.
 */
export const exportPeriodRange = (calendarDays, resultArray, cardsFor) => {
    const periods = new Map();
    resultArray.forEach(({ classes }) =>
        classes.forEach(({ class: period }) => {
            if (!periods.has(period.id)) periods.set(period.id, period);
        }),
    );

    const used = new Set();
    calendarDays.forEach((calendarDay) => {
        findDayData(resultArray, calendarDay)?.classes.forEach((classItem) => {
            if (cardsFor(calendarDay, classItem).some((g) => g.card !== null)) {
                used.add(classItem.class.id);
            }
        });
    });

    const ordered = [...periods.values()].sort(
        (a, b) => toMinutes(a.startTime) - toMinutes(b.startTime),
    );
    const first = ordered.findIndex((period) => used.has(period.id));
    if (first === -1) return [];

    let last = ordered.length - 1;
    while (!used.has(ordered[last].id)) last -= 1;

    return ordered.slice(first, last + 1);
};